import { motion } from 'motion/react';
import { useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import type { NotableOptionTradeRow } from '@/services/api';
import { optionTypeLabel, parseOccOptionSymbol } from '@/utils/parseOccOptionSymbol';

export function notableOptionTradeDiamondColor(trade: NotableOptionTradeRow): string {
  const t = trade.option_type ?? parseOccOptionSymbol(trade.symbol).optionType;
  if (t === 'C') return '#10b981';
  if (t === 'P') return '#ef4444';
  return '#d946ef';
}

export function NotableOptionTradeDiamond({
  cx,
  cy,
  trade,
  pulsate = false,
  sizeScale = 0.82,
  radiusPx,
  onClick,
}: {
  cx: number;
  cy: number;
  trade: NotableOptionTradeRow;
  pulsate?: boolean;
  sizeScale?: number;
  radiusPx?: number;
  onClick: (e: ReactMouseEvent) => void;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const color = notableOptionTradeDiamondColor(trade);
  const premium = Math.max(Number(trade.premium) || 0, 1);
  const fallbackR = Math.min(4 + Math.log10(premium / 25_000) * 2, 9) * sizeScale;
  const baseR = radiusPx != null && Number.isFinite(radiusPx) ? radiusPx : fallbackR;
  const r = baseR * sizeScale * 1.12;
  const hoverScale = isHovered ? 1.8 : 1;

  const ripple = (delay: number) => (
    <motion.g
      pointerEvents="none"
      style={{ transformOrigin: '0px 0px' }}
      initial={false}
      animate={{ scale: [1, 2.75], opacity: [0.42, 0] }}
      transition={{ duration: 4.2, repeat: Infinity, ease: 'easeOut', delay }}
    >
      <circle
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={0.65 * sizeScale}
      />
    </motion.g>
  );

  return (
    <g
      data-chart-interactive-marker="true"
      transform={`translate(${cx}, ${cy})`}
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ cursor: 'pointer' }}
      aria-label={`${optionTypeLabel(trade.option_type === 'C' || trade.option_type === 'P' ? trade.option_type : parseOccOptionSymbol(trade.symbol).optionType)} option trade`}
    >
      {pulsate && (
        <>
          {ripple(0)}
          {ripple(2.1)}
        </>
      )}
      <motion.g
        initial={false}
        animate={{ scale: hoverScale }}
        transition={{ duration: 0.14, ease: 'easeOut' }}
        style={{ transformOrigin: '0px 0px' }}
      >
        <circle
          cx={0}
          cy={0}
          r={r}
          fill={color}
          fillOpacity={0.9}
          stroke="#fff"
          strokeWidth={1.5 * sizeScale}
        />
      </motion.g>
      <circle r={Math.max(10, r * 2 + 2)} fill="transparent" />
    </g>
  );
}
